import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {useQuery} from '@tanstack/react-query';
import {
  AcademyData, AnalysisItem, BoundsObject, BusStopData, DaycareCenterData, HomeElderlyWelfareData,
  HospitalData, KindergartenData, PublicOfficeData, SchoolData, SubwayData, UniversityData
} from '../@types';
import {AreaAnalyticsFilter} from '../enums';
import {getAreaQueryVariables} from './analytics';

interface Variables extends BoundsObject {
  limit?: number;
}

export const useNearbySchool = (vars?: Variables & {types: string[]}) => {
  const request = useGqlRequest({
    query: QUERY_SCHOOL_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: SchoolData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: SchoolData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'school', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyAcademy = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_ACADEMY_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: AcademyData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: AcademyData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'academy', vars],
    enabled: !!vars,
    select,
    queryFn,
  });
};

export const useNearbyDaycareCenter = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_DAYCARE_CENTER_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: DaycareCenterData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: DaycareCenterData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'daycare', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyKindergarten = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_KINDER_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: KindergartenData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: KindergartenData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'kindergarten', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyUniversity = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUER_UNIVERSITIES_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: UniversityData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: UniversityData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'university', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyHospital = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_HOSPITAL_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: HospitalData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: HospitalData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'hospital', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyHomeElderlyWelfare = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_HOME_ELDERLY_WELFARES_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: HomeElderlyWelfareData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: HomeElderlyWelfareData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'homeelderwelfare', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyPublicOffice = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_PUBLIC_OFFICE_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: PublicOfficeData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: PublicOfficeData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'public-office', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbySubwayStation = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_SUBWAY_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: SubwayData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: SubwayData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'subway', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyBusStation = (vars?: Variables) => {
  const request = useGqlRequest({
    query: QUERY_BUS_BY_BOUNDS,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: BusStopData[];
      }> => {
    if (!vars) {
      return;
    }

    return request(vars);
  };

  const select = (data?: {result?: BusStopData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'bus', vars],
    enabled: !!vars,
    queryFn,
    select,
  });
};

export const useNearbyChildcareCenterByPoint = (item: AnalysisItem) => {
  const request = useGqlRequest({
    query: QUERY_CHILDCARE_CENTER_BY_POINT,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        [AreaAnalyticsFilter.DAYCARE_CENTER]?: DaycareCenterData[];
        [AreaAnalyticsFilter.KINDERGARTEN]?: KindergartenData[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant?.radius) {
      return;
    }

    const {radius, ...vars} = variant;

    return request({...vars, distance: radius / 1000});
  };

  const id = item?.id;

  return useQuery({
    queryKey: ['nearby', 'childcare', id],
    enabled: !!id,
    queryFn,
  });
};

export const useNearbySchoolByPoint = (item: AnalysisItem) => {
  const request = useGqlRequest({
    query: QUERY_SCHOOL_BY_POINT,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        school?: Omit<SchoolData, 'gradeStudentCount'>[];
        [AreaAnalyticsFilter.UNIVERSITY]: UniversityData[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant?.radius) {
      return;
    }

    const {radius, ...vars} = variant;

    return request({...vars, distance: radius / 1000});
  };

  const id = item?.id;

  return useQuery({
    queryKey: ['nearby', 'school', id],
    enabled: !!id,
    queryFn,
  });
};

export const useNearbyHospitalByPoint = (item: AnalysisItem) => {
  const request = useGqlRequest({
    query: QUERY_HOSPITAL_BY_POINT,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: HospitalData[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant?.radius) {
      return;
    }

    const {radius, ...vars} = variant;

    return request({...vars, distance: radius / 1000});
  };

  const id = item?.id;

  const select = (data?: {result?: HospitalData[]}) => data?.result;

  return useQuery({
    queryKey: ['nearby', 'hospital', id],
    enabled: !!id,
    queryFn,
    select,
  });
};

export const useBusSubwayByPoint = (item: AnalysisItem) => {
  const request = useGqlRequest({
    query: QUERY_BUS_SUBWAY_BY_POINT,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        [AreaAnalyticsFilter.BUS]?: BusStopData[];
        [AreaAnalyticsFilter.SUBWAY]?: SubwayData[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant?.radius) {
      return;
    }

    const {radius, ...vars} = variant;

    return request({...vars, distance: radius / 1000});
  };

  const id = item?.id;

  return useQuery({
    queryKey: ['nearby', 'bus-subway', id],
    enabled: !!id,
    queryFn,
  });
};

const QUERY_VARS = `$lat1: Float!, $lng1: Float!, $lat2: Float!, $lng2: Float!`;
const QUERY_RESULT_VARS = `lat1: $lat1, lng1: $lng1, lat2: $lat2, lng2: $lng2`;

const NEARBY_BASE_RESULT = `
  name
  location {
    coordinates
  }
`;

const SCHOOLE_RESULT = `
  ${NEARBY_BASE_RESULT}
  studentsCount
`;

const QUERY_SCHOOL_BY_BOUNDS = gql`
  query schoolNearBy($types: [SchoolTypeArgs!]!, ${QUERY_VARS}) {
    result: schoolNearBy(types: $types, ${QUERY_RESULT_VARS}) {
      ${SCHOOLE_RESULT}
      type
      gradeStudentCount
    }
  }
`;

const QUERY_KINDER_BY_BOUNDS = gql`
  query kindergartenNearBy($limit: Float, ${QUERY_VARS}) {
    result: kindergartenNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${SCHOOLE_RESULT}
    }
  }
`;

const QUER_UNIVERSITIES_BY_BOUNDS = gql`
  query universityNearBy($limit: Float, ${QUERY_VARS}) {
    result: universityNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${SCHOOLE_RESULT}
    }
  }
`;

const QUERY_ACADEMY_BY_BOUNDS = gql`
  query academyNearBy($limit: Float, ${QUERY_VARS}) {
    result: academyNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${NEARBY_BASE_RESULT}
      category
    }
  }
`;

const QUERY_DAYCARE_CENTER_BY_BOUNDS = gql`
  query daycareCenterNearBy($limit: Float, ${QUERY_VARS}) {
    result: daycareCenterNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${NEARBY_BASE_RESULT}
      childrenCount
    }
  }
`;

const QUERY_HOSPITAL_BY_BOUNDS = gql`
  query hospitalNearBy($limit: Float, ${QUERY_VARS}) {
    result: hospitalNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${NEARBY_BASE_RESULT}
      bedsCount
    }
  }
`;

const QUERY_HOME_ELDERLY_WELFARES_BY_BOUNDS = gql`
  query homeCareNearBy($limit: Float, ${QUERY_VARS}) {
    result: homeCareNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${NEARBY_BASE_RESULT}
      salesTypeCount
      salesTypes
      limitMemberCount
      memberCount
    }
  }
`;

const QUERY_PUBLIC_OFFICE_BY_BOUNDS = gql`
  query publicOfficeNearBy( ${QUERY_VARS}, $limit: Float) {
    result: publicOfficeNearBy(${QUERY_RESULT_VARS}, limit: $limit) {
      ${NEARBY_BASE_RESULT}
      type
      typeLabel
    }
  }
`;

const QUERY_SUBWAY_BY_BOUNDS = gql`
  query subwayNearBy($limit: Float, ${QUERY_VARS}) {
    result: subwayNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      ${NEARBY_BASE_RESULT}
      subways {
        getOnOffAvg
        line
        name
      }
    }
  }
`;

const QUERY_BUS_BY_BOUNDS = gql`
  query busStopNearBy($limit: Float, ${QUERY_VARS}) {
    result: busStopNearBy(limit: $limit, ${QUERY_RESULT_VARS}) {
      stopName
      location {
        coordinates
      }
      avgNumberOfRidesPerDay
    }
  }
`;

const QUERY_VARS_POINT = `$distance: Float!, $lat: Float!, $lng: Float!`;
const QUERY_RESULT_VARS_POINT = `distance: $distance, lat: $lat, lng: $lng`;

const QUERY_CHILDCARE_CENTER_BY_POINT = gql`
  query childcareCenterPointBy(${QUERY_VARS_POINT}) {
    ${AreaAnalyticsFilter.DAYCARE_CENTER}: dayCareCenterPointBy(${QUERY_RESULT_VARS_POINT}) {
      ${NEARBY_BASE_RESULT}
      childrenCount
    }
    ${AreaAnalyticsFilter.KINDERGARTEN}: kindergartenPointBy(${QUERY_RESULT_VARS_POINT}) {
      ${NEARBY_BASE_RESULT}
      studentsCount
    }
  }
`;

const QUERY_SCHOOL_BY_POINT = gql`
  query schoolPointBy(${QUERY_VARS_POINT}) {
    school: schoolPointBy(${QUERY_RESULT_VARS_POINT}, type: ["초등학교", "중학교", "고등학교"]) {
      ${NEARBY_BASE_RESULT}
      studentsCount
      type
    }
    ${AreaAnalyticsFilter.UNIVERSITY}: universityPointBy(${QUERY_RESULT_VARS_POINT}) {
      ${NEARBY_BASE_RESULT}
      studentsCount
    }
  }
`;

const QUERY_HOSPITAL_BY_POINT = gql`
  query hospitalPointBy(${QUERY_VARS_POINT}) {
    result: hospitalPointBy(${QUERY_RESULT_VARS_POINT}) {
      ${NEARBY_BASE_RESULT}
      bedsCount
    }
  }
`;

const QUERY_BUS_SUBWAY_BY_POINT = gql`
  query busAndSubwayPointBy(${QUERY_VARS_POINT}) {
    ${AreaAnalyticsFilter.BUS}: busStopPointBy(${QUERY_RESULT_VARS_POINT}) {
      stopName
      location {
        coordinates
      }
      avgNumberOfRidesPerDay
    }
    ${AreaAnalyticsFilter.SUBWAY}: subwayPointBy(${QUERY_RESULT_VARS_POINT}) {
      ${NEARBY_BASE_RESULT}
      subways {
        getOnOffAvg
        line
        name
      }
    }
  }
`;
